import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';

/** Page content hidden below the fold must be at least this share of the viewport. */
const MIN_HIDDEN_FRACTION = 0.25;
/** Any scroll beyond this hides the cue — the visitor has found the rest. */
const SCROLLED_PX = 40;
/** Wait for lazy pages, CMS content and hero animations before measuring. */
const SETTLE_MS = 1200;

/**
 * Site-wide animated "scroll down" cue. Shown at the top of any page whose
 * content continues well below the first screen, so a hero that fills the
 * viewport never looks like the end of the page. It measures the live layout
 * (per device, per page) instead of a hard-coded page list:
 *   - only the page's own content (<main>) counts — a footer alone below the
 *     fold doesn't need a cue;
 *   - pages that render their own cue mark it `data-scroll-cue`; while one is
 *     on screen this one stays hidden;
 *   - it disappears as soon as the visitor scrolls.
 */
export default function ScrollCue() {
  const { pathname } = useLocation();
  const [settled, setSettled] = useState(false);
  const [needed, setNeeded] = useState(false);

  useEffect(() => {
    setSettled(false);
    setNeeded(false);
    const settleTimer = window.setTimeout(() => setSettled(true), SETTLE_MS);

    const measure = () => {
      const main = document.querySelector('main');
      if (!main || window.scrollY > SCROLLED_PX) {
        setNeeded(false);
        return;
      }
      const viewport = window.innerHeight;
      const hiddenBelow = main.getBoundingClientRect().bottom - viewport;
      const ownCueVisible = Array.from(document.querySelectorAll('[data-scroll-cue]')).some((el) => {
        const r = el.getBoundingClientRect();
        return r.height > 0 && r.top < viewport && r.bottom > 0;
      });
      setNeeded(hiddenBelow > viewport * MIN_HIDDEN_FRACTION && !ownCueVisible);
    };

    measure();
    const resizeObserver = new ResizeObserver(measure);
    resizeObserver.observe(document.body);
    window.addEventListener('scroll', measure, { passive: true });
    window.addEventListener('resize', measure);
    return () => {
      window.clearTimeout(settleTimer);
      resizeObserver.disconnect();
      window.removeEventListener('scroll', measure);
      window.removeEventListener('resize', measure);
    };
  }, [pathname]);

  // Build-time prerender (scripts/prerender.js) snapshots the DOM in headless
  // Chrome — keep the cue out of the static HTML.
  if (/HeadlessChrome/.test(navigator.userAgent)) return null;

  const visible = settled && needed;

  return (
    <button
      type="button"
      onClick={() => window.scrollBy({ top: Math.round(window.innerHeight * 0.8), behavior: 'smooth' })}
      aria-label="Scroll down for more"
      aria-hidden={!visible}
      tabIndex={visible ? 0 : -1}
      // Phones: lifted above the floating WhatsApp button, which spans most
      // of the bottom edge there. sm+: bottom-centre, clear of it.
      className={`fixed left-1/2 -translate-x-1/2 bottom-[6.5rem] sm:bottom-6 z-40 flex flex-col items-center
        rounded-full bg-white/90 backdrop-blur-sm px-3 pt-1.5 pb-1 text-gray-700 shadow-lg ring-1 ring-black/5
        hover:bg-white hover:text-primary transition-all duration-500
        ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3 pointer-events-none'}`}
    >
      <span className="text-[10px] font-semibold uppercase tracking-[0.2em]">Scroll</span>
      <ChevronDown className="w-6 h-6 animate-bounce" />
    </button>
  );
}
